import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSite, getSiteUsers, clearSite } from 'app/redux/slices/siteSlice';
import { getOrgById, clearOrg, getAllUsersByOrgId, clearOrgUserList } from 'app/redux/slices/orgSlice';
import { determineHighestPrivilege } from 'app/utils';
import { SITE_ROLES } from 'app/utils/constant';
import useAuth from 'app/hooks/useAuth';

const SiteContext = createContext(null);

export const SiteProvider = ({ siteId, children }) => {
    const dispatch = useDispatch();
    const { user } = useAuth();

    const siteInfo = useSelector(state => state.site.site);
    const siteUsers = useSelector(state => state.site.siteUsersList);
    const error = useSelector(state => state.site.error);
    const unauthorized = useSelector(state => state.site.unauthorized);
    const org = useSelector(state => state.orgs.org);

    const [highestPrivilege, setHighestPrivilege] = useState(null);
    const [loading, setLoading] = useState(true);
    const lastSiteIdRef = useRef();
    const [rolesReady, setRolesReady] = useState(false);

    // Fetch site info and users
    useEffect(() => {
        if (!siteId || lastSiteIdRef.current === siteId) return;
        lastSiteIdRef.current = siteId;

        const fetchSiteData = async () => {
            setLoading(true);
            try {
                await dispatch(getSite(siteId));
                await dispatch(getSiteUsers({ siteId }));
            } catch (err) {
                console.error('Error fetching site data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSiteData();

        return () => {
            dispatch(clearSite());
        };
        // eslint-disable-next-line
    }, [siteId]);

    // Memoized current user site role
    const currentUserSiteRole = useMemo(() => {
        if (user && siteUsers) {
            const foundUser = siteUsers.find(su => su.id === user.id);
            return SITE_ROLES[Number(foundUser?.siteRole)] || 'No Role';
        }
        return 'No Role';
    }, [user, siteUsers]);

    // Calculate and validate role readiness
    useEffect(() => {
        const valid = user?.userRole && typeof currentUserSiteRole === 'string';
        setRolesReady(valid);
    }, [user?.userRole, currentUserSiteRole]);

    // Calculate the highest privilege for the user
    useEffect(() => {
        if (!rolesReady) return;
        const userRoles = {
            userRole: user.userRole,
            orgRole: org?.orgRole || 0,
            siteRole: currentUserSiteRole,
        };

        setHighestPrivilege(determineHighestPrivilege(userRoles));
    }, [rolesReady, user, org, currentUserSiteRole]);

    // Fetch organization data if not a demo site
    useEffect(() => {
        if (!siteInfo || siteInfo.isDemo) return;

        if (siteInfo.orgId) {
            dispatch(getOrgById(parseInt(siteInfo.orgId, 10)));
            dispatch(getAllUsersByOrgId(siteInfo.orgId));
        }

        return () => {
            dispatch(clearOrg());
            dispatch(clearOrgUserList());
        };
        // eslint-disable-next-line
    }, [siteInfo]);

    const contextValue = useMemo(() => ({
        siteInfo,
        highestPrivilege,
        loading,
        error,
        unauthorized,
        org,
        currentUserSiteRole,
    }), [siteInfo, highestPrivilege, loading, error, unauthorized, org, currentUserSiteRole]);

    return (
        <SiteContext.Provider value={contextValue}>
            {children}
        </SiteContext.Provider>
    );
};

export const useSite = () => useContext(SiteContext);
